import { useEffect, useRef, useState, type CSSProperties, type ChangeEvent, type ReactNode } from "react";
import { Icon, Tooltip } from "@bedrock/ui";

export interface VideoMetadata {
  width: number;
  height: number;
  duration: number;
}

export function formatVideoTime(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0:00";
  const totalSeconds = Math.floor(value);
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function VideoGlyph({ name }: { name: "play" | "pause" | "muted" | "sound" | "fullscreen" }): ReactNode {
  if (name === "play") return <svg data-video-glyph="play" viewBox="0 0 24 24" aria-hidden="true"><path d="m8.25 5.8 9.5 6.2-9.5 6.2V5.8Z"/></svg>;
  if (name === "pause") return <svg data-video-glyph="pause" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.25 6.25v11.5M15.75 6.25v11.5"/></svg>;
  if (name === "muted") return <svg data-video-glyph="muted" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 10h3l4-3.5v11L8 14H5v-4ZM16 10l4 4M20 10l-4 4"/></svg>;
  if (name === "sound") return <svg data-video-glyph="sound" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 10h3l4-3.5v11L8 14H5v-4ZM16 9.25a4.25 4.25 0 0 1 0 5.5M18.5 7a7.25 7.25 0 0 1 0 10"/></svg>;
  return <svg data-video-glyph="fullscreen" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4.5H5.5a1 1 0 0 0-1 1V9M15 4.5h3.5a1 1 0 0 1 1 1V9M9 19.5H5.5a1 1 0 0 1-1-1V15M15 19.5h3.5a1 1 0 0 0 1-1V15"/></svg>;
}

interface VideoPlayerProps {
  src?: string | undefined;
  filename: string;
  variant: "canvas" | "detail";
  autoPlay?: boolean;
  onMetadata?: (metadata: VideoMetadata) => void;
}

export function VideoPlayer({ src, filename, variant, autoPlay = false, onMetadata }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => setPlaying(false));
    else video.pause();
  };

  const seek = (event: ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    const nextTime = Number(event.currentTarget.value);
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setMuted(nextMuted);
  };

  const toggleFullscreen = () => {
    const player = playerRef.current;
    if (!player) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void player.requestFullscreen();
  };

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  return <div ref={playerRef} className={`video-player video-player--${variant} ${playing ? "is-playing" : "is-paused"}`} aria-label={`${filename} video player`}>
    {src ? <video
      ref={videoRef}
      className="video-player__media"
      src={src}
      preload={variant === "canvas" ? "metadata" : "auto"}
      autoPlay={autoPlay}
      playsInline
      draggable={false}
      onClick={togglePlayback}
      onLoadedMetadata={(event) => {
        const video = event.currentTarget;
        const nextDuration = Number.isFinite(video.duration) ? video.duration : 0;
        setDuration(nextDuration);
        setMuted(video.muted);
        onMetadata?.({ width: video.videoWidth, height: video.videoHeight, duration: nextDuration });
      }}
      onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      onEnded={() => setPlaying(false)}
    /> : <div className="video-player__placeholder"><Icon name="video"/><span>Video unavailable</span></div>}
    <div className="video-player__shade" aria-hidden="true"/>
    {variant === "canvas" && <div className="video-player__identity" aria-hidden="true"><span>Video</span><strong>{filename}</strong></div>}
    {src && <Tooltip label={playing ? "Pause video" : "Play video"}><button type="button" className="video-player__poster-toggle" aria-label={playing ? "Pause video" : "Play video"} onPointerDown={(event) => event.stopPropagation()} onClick={togglePlayback}><VideoGlyph name={playing ? "pause" : "play"}/></button></Tooltip>}
    {src && <div className="video-player__controls" onPointerDown={(event) => event.stopPropagation()}>
      <Tooltip label={playing ? "Pause video" : "Play video"}><button type="button" aria-label={playing ? "Pause video" : "Play video"} onClick={togglePlayback}><VideoGlyph name={playing ? "pause" : "play"}/></button></Tooltip>
      <input
        type="range"
        min="0"
        max={Math.max(duration, .01)}
        step="0.01"
        value={Math.min(currentTime, Math.max(duration, .01))}
        aria-label="Seek video"
        onChange={seek}
        style={{ "--video-progress": `${progress}%` } as CSSProperties}
      />
      <output>{formatVideoTime(currentTime)} <span>/</span> {formatVideoTime(duration)}</output>
      <Tooltip label={muted ? "Unmute video" : "Mute video"}><button type="button" className="video-player__sound" aria-label={muted ? "Unmute video" : "Mute video"} onClick={toggleMute}><VideoGlyph name={muted ? "muted" : "sound"}/></button></Tooltip>
      {variant === "detail" && <Tooltip label="View fullscreen"><button type="button" className="video-player__fullscreen" aria-label="View video fullscreen" onClick={toggleFullscreen}><VideoGlyph name="fullscreen"/></button></Tooltip>}
    </div>}
  </div>;
}
